import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, PlusCircle, Send, Rocket, MapPin, DollarSign, Palette, CheckCircle2, XCircle, UsersRound, FileText } from "lucide-react";
import StatusTracker from "@/components/design/StatusTracker";
import InteractionLogsPanel from "@/components/design/InteractionLogsPanel";
import FileUploadCards from "@/components/design/FileUploadCards";
import ReassignDesignTeamModal from "@/components/design/ReassignDesignTeamModal";
import AddHelperDesignerModal from "@/components/design/AddHelperDesignerModal";
import CrmOnboardingPanel from "@/components/design/CrmOnboardingPanel";
import SlaAndSetsPanel from "@/components/design/SlaAndSetsPanel";
import TeamAssignmentPanel from "@/components/shared/TeamAssignmentPanel";
import FactoryStatusPanel from "@/components/shared/FactoryStatusPanel";

export default function DesignProjectDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { principal, isAdmin, roleName, roleType } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReassignFull, setShowReassignFull] = useState(false); // with reason (DH/Admin/Director)
  const [showAddHelper, setShowAddHelper] = useState(false);

  const canEdit = isAdmin || roleType === "director" || roleName === "Design Head" ||
    (roleName === "Design Manager" && project?.design_manager_id === principal?.id) ||
    (roleName === "Interior Designer" &&
      (project?.interior_designer_id === principal?.id ||
       (project?.helper_designer_ids || []).includes(principal?.id)));

  // Full team reassign (with reason) — DH / Admin / Director.
  const canReassignFull = isAdmin || roleType === "director" || roleName === "Design Head";
  // Add helper — DM (own project), DH, Admin, Director.
  const canAddHelper = isAdmin || roleType === "director" || roleName === "Design Head" ||
    (roleName === "Design Manager" && project?.design_manager_id === principal?.id);

  // CRM Onboarding — CRM Manager / CRM Executive / Admin / Director can fill it in.
  const isCrmUser = isAdmin || roleType === "director"
    || roleName === "CRM Manager" || roleName === "CRM Executive";

  const isFinanceAuth = isAdmin || roleType === "director" || roleName === "Design Head";

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/design/projects/${id}`);
      setProject(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      nav("/design/projects");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [id]);

  const changeStatus = async (status) => {
    try {
      await api.patch(`/design/projects/${id}/status`, { status });
      toast.success("Status updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const incrementIter = async (fileDataUrl, fileName) => {
    try {
      const payload = fileDataUrl ? { data_url: fileDataUrl, name: fileName } : null;
      await api.post(`/design/projects/${id}/increment-iteration`, payload);
      toast.success(fileDataUrl ? "New variation shared with attachment" : "Iteration counter +1");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onVariationFile = async (files) => {
    if (!files?.[0]) { await incrementIter(); return; }
    const file = files[0];
    const reader = new FileReader();
    reader.onload = () => incrementIter(reader.result, file.name);
    reader.readAsDataURL(file);
  };

  const pushSignoff = async () => {
    try {
      await api.post(`/design/projects/${id}/push-signoff`);
      toast.success("Sign-off pushed to client app (MOCKED)");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const toggle45 = async () => {
    try {
      await api.post(`/design/projects/${id}/finance-45`, { is_paid: !project.is_45_percent_paid });
      toast.success(`45% payment ${!project.is_45_percent_paid ? "confirmed" : "cleared"}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const moveToProduction = async () => {
    try {
      await api.post(`/design/projects/${id}/move-to-production`);
      toast.success("🏗️ Moved to Production — IMOS Manager notified");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  if (loading || !project) {
    return <div className="p-10 text-gray-500 text-sm">Loading…</div>;
  }

  // final_execution_set may be a single legacy object or the new array (multi-file). Any non-empty state satisfies the gate.
  const finalRaw = (project.files || {}).final_execution_set;
  const hasFinal = Array.isArray(finalRaw) ? finalRaw.length > 0 : !!finalRaw;
  const signoffRaw = (project.files || {}).signoff_sheet;
  const hasSignoff = Array.isArray(signoffRaw) ? signoffRaw.length > 0 : !!signoffRaw;
  const isPaid = !!project.is_45_percent_paid;
  const canMoveToProd = canEdit && hasFinal && isPaid && !project.production_ready;

  return (
    <div className="min-h-full">
      <div className="border-b border-[#262626] px-10 py-5 flex items-center justify-between bg-[#050505]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="flex items-center gap-4">
          <button onClick={() => nav(-1)} className="iz-btn-secondary p-2" data-testid="back-btn"><ArrowLeft className="w-4 h-4"/></button>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Design Project</p>
            <h1 className="font-display text-2xl text-white mt-1">{project.client_name}{project.city ? ` · ${project.city}` : ""}</h1>
            <p className="text-xs text-gray-500 mt-0.5">{project.project_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {project.production_ready ? (
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#d4af37] bg-[#d4af37]/10 border border-[#d4af37]/30 rounded px-3 py-1.5 flex items-center gap-2">
              <Rocket className="w-3 h-3"/> In Production
            </span>
          ) : (
            <button
              onClick={moveToProduction}
              disabled={!canMoveToProd}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${
                canMoveToProd ? "bg-[#d4af37] text-black hover:bg-[#c5a059]" : "bg-[#1a1a1a] text-gray-600 cursor-not-allowed border border-[#262626]"
              }`}
              data-testid="move-to-production"
              title={canMoveToProd ? "Move to Production" : "Requires final execution set + 45% payment"}
            >
              <Rocket className="w-4 h-4"/> Move to Production
            </button>
          )}
        </div>
      </div>

      <div className="px-10 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Overview */}
          <div className="iz-card p-6">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500">Overview</p>
              <div className="flex items-center gap-2 flex-wrap">
                {canReassignFull && (
                  <button
                    onClick={() => setShowReassignFull(true)}
                    className="iz-btn-primary text-xs flex items-center gap-1.5"
                    data-testid="reassign-full-btn"
                  >
                    <UsersRound className="w-3.5 h-3.5" />
                    {project.design_manager_id ? "Reassign Manager" : "Assign Manager"}
                  </button>
                )}
                {canAddHelper && (
                  <button
                    onClick={() => setShowAddHelper(true)}
                    className="iz-btn-secondary text-xs flex items-center gap-1.5"
                    data-testid="add-helper-btn"
                  >
                    <UsersRound className="w-3.5 h-3.5" />
                    Add Helper
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="City" value={project.city || "—"} icon={MapPin}/>
              <Field label="Flat No." value={project.flat_no || "—"}/>
              <Field label="Address" value={project.address || "—"} col2/>
              <Field label="Project Amount" value={`₹${(project.project_amount || 0).toLocaleString()}`} gold icon={DollarSign}/>
              <Field label="Amount Received" value={`₹${(project.amount_received || 0).toLocaleString()}`}/>
              <Field label="Design Head" value={project._names?.design_head || (project.design_head_id ? "Unnamed" : "—")}/>
              <Field label="Design Manager" value={project._names?.design_manager || (project.design_manager_id ? "Unnamed" : "Unassigned")} icon={Palette}/>
              <Field
                label="Interior Designer"
                value={project.interior_designer_id
                  ? (project._names?.interior_designer || "Unnamed")
                  : (project.design_manager_id
                      ? `Handled by Design Manager · ${project._names?.design_manager || "…"}`
                      : "Unassigned")}
                col2
              />
            </div>
            {Array.isArray(project._names?.helper_designers) && project._names.helper_designers.length > 0 && (
              <div className="mt-4 pt-3 border-t border-[#1a1a1a]" data-testid="helper-designers-panel">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-1">Helper Designers</p>
                <p className="text-xs text-gray-300">
                  {project._names.helper_designers.map(h => h.name).join(" · ")}
                </p>
              </div>
            )}
            {(project.originating_sales_manager_id || project.originating_sales_executive_id) && (
              <div className="mt-4 pt-3 border-t border-[#1a1a1a]">
                <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-1">Lineage · From Sales</p>
                <p className="text-[11px] text-gray-400 font-mono">
                  {project._names?.originating_sales_manager ? `SM · ${project._names.originating_sales_manager}` : "—"}
                  {" · "}
                  {project._names?.originating_sales_executive ? `SE · ${project._names.originating_sales_executive}` : "—"}
                </p>
              </div>
            )}
          </div>

          {/* SLA + 3D/2D Set-Shared tracker */}
          <SlaAndSetsPanel projectId={project.id} onChanged={load}/>

          {/* Won attachments (from the origin sales_lead) — view + download */}
          {project.won_attachments && (
            <div className="iz-card p-6" data-testid="won-attachments-panel">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-1">From Sales · Won Package</p>
              <p className="text-xs text-gray-400 mb-4">
                Uploaded by the Sales Executive when the lead was closed. Available to every stakeholder on the project.
              </p>
              {project.won_attachments.sales_mom && (
                <div className="mb-4 pb-4 border-b border-[#1a1a1a]">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-1">Sales Minutes of Meeting</p>
                  <p className="text-sm text-white whitespace-pre-wrap">{project.won_attachments.sales_mom}</p>
                </div>
              )}
              <div className="flex flex-wrap gap-2 mb-3">
                {project.won_attachments.floor_plan_pdf && (
                  <a
                    href={project.won_attachments.floor_plan_pdf}
                    download="floor_plan.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="iz-btn-secondary text-xs flex items-center gap-1.5"
                    data-testid="dl-floor-plan"
                  >
                    <FileText className="w-3.5 h-3.5"/> Floor Plan PDF
                  </a>
                )}
                {project.won_attachments.initial_quotation_pdf && (
                  <a
                    href={project.won_attachments.initial_quotation_pdf}
                    download="initial_quotation.pdf"
                    target="_blank"
                    rel="noreferrer"
                    className="iz-btn-secondary text-xs flex items-center gap-1.5"
                    data-testid="dl-initial-quotation"
                  >
                    <FileText className="w-3.5 h-3.5"/> Initial Quotation PDF
                  </a>
                )}
              </div>
              {project.won_attachments.reference_images?.length > 0 && (
                <div>
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">
                    Reference Images ({project.won_attachments.reference_images.length})
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {project.won_attachments.reference_images.map((src, i) => (
                      <a key={`refimg-${i}-${(src || "").slice(-24)}`} href={src} download={`reference-${i + 1}`} target="_blank" rel="noreferrer" className="block" data-testid={`dl-ref-${i}`}>
                        <img src={src} alt={`ref-${i}`} className="w-full h-20 object-cover rounded border border-[#262626] hover:border-[#d4af37]/60"/>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Reassignment audit — visible to Admin, Director, Design Head only */}
          {canReassignFull && Array.isArray(project.reassignment_history) && project.reassignment_history.length > 0 && (
            <div className="iz-card p-6" data-testid="reassignment-history-panel">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Reassignment Audit</p>
              <ul className="space-y-3">
                {project.reassignment_history.slice().reverse().map((h, i) => (
                  <li key={h.at ? `${h.at}-${i}` : `reassign-${i}`} className="border border-[#1a1a1a] rounded-md p-3">
                    <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                      {new Date(h.at).toLocaleString()}
                    </p>
                    <p className="text-sm text-white mt-1 italic">"{h.reason || "(reason hidden)"}"</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* CRM Onboarding — visible to CRM Executive/Manager, Admin, Director.
              For everyone else, only show when already completed (read-only overview). */}
          {(isCrmUser || project.crm_onboarded_at) && (
            <CrmOnboardingPanel project={project} canEdit={isCrmUser} onChanged={load} />
          )}

          {/* Project Team panel — CRM (edit) / IMOS (read-only) */}
          <TeamAssignmentPanel projectId={project.id} onChanged={load} />

          {/* Factory Status — silently hidden until order reaches Sent-to-Factory */}
          <FactoryStatusPanel projectId={project.id} />

          {/* Files */}
          <div className="iz-card p-6">
            <FileUploadCards project={project} canEdit={canEdit} onChanged={load}/>
          </div>

          {/* Actions */}
          {canEdit && !project.production_ready && (
            <div className="iz-card p-6">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-gray-500 mb-3">Designer Actions</p>
              <div className="flex flex-wrap gap-3">
                <label className="iz-btn-secondary flex items-center gap-2 text-xs cursor-pointer" data-testid="incr-iter">
                  <PlusCircle className="w-3.5 h-3.5"/>Share new variation ({project.iteration_count})
                  <input
                    type="file"
                    accept="application/pdf,image/*,.dwg,.dxf"
                    hidden
                    onChange={(e) => { onVariationFile(e.target.files); e.target.value = ""; }}
                    data-testid="incr-iter-file"
                  />
                </label>
                <button
                  onClick={pushSignoff}
                  disabled={!hasSignoff}
                  className="iz-btn-primary flex items-center gap-2 text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="push-signoff"
                  title={hasSignoff ? "Push Sign-Off Sheet to Client App (MOCKED)" : "Upload sign-off sheet first"}
                >
                  <Send className="w-3.5 h-3.5"/>Push Sign-Off to Client App
                </button>
                {project.signoff_pushed_at && (
                  <span className="text-[10px] font-mono text-[#10b981] flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3"/> Sent {new Date(project.signoff_pushed_at).toLocaleString()}
                  </span>
                )}
              </div>
              {Array.isArray(project.variations) && project.variations.length > 0 && (
                <div className="mt-4 pt-3 border-t border-[#1a1a1a]" data-testid="variations-list">
                  <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mb-2">Variation attachments</p>
                  <ul className="space-y-1.5">
                    {project.variations.slice().reverse().map((v, i) => (
                      <li key={v.id ?? `var-${v.iteration_no ?? i}`} className="text-xs flex items-center gap-2">
                        <span className="text-[#d4af37] font-mono">#{v.iteration_no}</span>
                        <a href={v.data_url} target="_blank" rel="noreferrer" className="text-[#d4af37] hover:underline truncate flex-1">
                          {v.name || "variation"}
                        </a>
                        <span className="text-[10px] font-mono text-gray-600">{new Date(v.uploaded_at).toLocaleDateString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="iz-card p-6">
            <InteractionLogsPanel project={project} canEdit={canEdit} onChanged={load}/>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6">
          <div className="iz-card p-6">
            <StatusTracker project={project} canEdit={canEdit && !project.production_ready} onChange={changeStatus}/>
          </div>

          {/* Production Gate & Finance */}
          <div className="iz-card p-6">
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37] mb-3">Production Gate</p>
            <div className="space-y-3">
              <GateRow ok={hasFinal} label="Final execution set uploaded"/>
              <GateRow ok={isPaid} label="45% payment confirmed"/>
            </div>
            {isFinanceAuth && (
              <div className="mt-4 pt-4 border-t border-[#262626]">
                <p className="text-[9px] font-mono uppercase tracking-widest text-gray-500 mb-2">Simulated finance action</p>
                <button onClick={toggle45} className="iz-btn-secondary w-full text-xs flex items-center justify-center gap-2" data-testid="toggle-45">
                  {isPaid ? "Clear 45% payment flag" : "Mark 45% payment received"}
                </button>
                <p className="text-[10px] text-gray-600 mt-2 font-mono italic">MOCKED Finance action · Real integration in Phase 4</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {canReassignFull && (
        <ReassignDesignTeamModal
          open={showReassignFull}
          project={project}
          onClose={() => setShowReassignFull(false)}
          onSaved={() => { setShowReassignFull(false); load(); }}
        />
      )}
      {canAddHelper && (
        <AddHelperDesignerModal
          open={showAddHelper}
          project={project}
          onClose={() => setShowAddHelper(false)}
          onSaved={() => { setShowAddHelper(false); load(); }}
        />
      )}
    </div>
  );
}

function Field({ label, value, gold, icon: Icon, col2 }) {
  return (
    <div className={col2 ? "col-span-2" : ""}>
      <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">{label}</p>
      <p className={`mt-1 flex items-center gap-1.5 ${gold ? "text-[#d4af37]" : "text-white"}`}>
        {Icon && <Icon className="w-3.5 h-3.5"/>}
        <span className="text-sm">{value}</span>
      </p>
    </div>
  );
}

function GateRow({ ok, label }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {ok ? <CheckCircle2 className="w-4 h-4 text-[#d4af37]"/> : <XCircle className="w-4 h-4 text-gray-600"/>}
      <span className={ok ? "text-white" : "text-gray-500"}>{label}</span>
    </div>
  );
}
